export function toArabicNumerals(n: number | string): string {
  return String(n).replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[+d]);
}

export function formatRating(rating: number): string {
  return toArabicNumerals(rating.toFixed(1).replace(".", "٫"));
}

// Arabic count agreement for «فرع» (branch): 1 singular, 2 dual, 3–10 plural, 11+ accusative.
//
// Lives here rather than in StoreBranchesCard because both that component and StorePageHeader
// need it, and exporting a non-component from a component file trips react-refresh lint.
//
// `formatNumber` lets the caller choose Arabic-Indic digits (the store header) or Latin ones
// (the branches sheet, matching the dir="ltr" numeral treatment in its rows). «فرع واحد» and
// «فرعان» carry no numeral, so the formatter is correctly a no-op for 1 and 2.
//
// Callers must keep this as a STANDALONE phrase, never governed by a preposition: the dual
// «فرعان» is nominative and would have to become «فرعين» after «من».
export function branchCountLabel(
  count: number,
  formatNumber: (value: number) => string = String,
): string {
  if (count === 1) return "فرع واحد";
  if (count === 2) return "فرعان";
  if (count <= 10) return `${formatNumber(count)} فروع`;
  return `${formatNumber(count)} فرعاً`;
}

// ─── Humanized counts ───

/**
 * DIGIT SYSTEM FOR HUMANIZED COUNTS. Flip to false for Western digits («14.7 ألف»).
 *
 * The brief asked for Western digits, but every other number already on these pages goes through
 * `toLocaleString("ar-EG")` and renders Arabic-Indic («٣٬٣٧٨»). Only one of the two can be right
 * on a single card: Western followers sitting next to an Arabic-Indic page count reads as a bug,
 * not as a choice. So this ships Arabic-Indic, matching `formatRating` above and the existing
 * public-site precedent.
 *
 * It is ONE CONSTANT on purpose — reversing the decision is this line plus its test, not a sweep
 * through every call site.
 */
const ARABIC_INDIC_DIGITS = true;

/** U+066B ARABIC DECIMAL SEPARATOR, matching `formatRating`. Not a Latin dot, and not U+060C. */
const ARABIC_DECIMAL_SEPARATOR = "٫";

const formatHumanizedNumber = (value: string): string =>
  ARABIC_INDIC_DIGITS
    ? toArabicNumerals(value.replace(".", ARABIC_DECIMAL_SEPARATOR))
    : value;

/**
 * «١٤٫٧ ألف» / «١٫١ مليون» for vanity metrics.
 *
 * WHERE THIS BELONGS, AND WHERE IT MUST NOT BE USED. It is for `followersCount` and
 * `graph.ratingCount` — numbers read off a stale `graph.*` subdoc that nothing maintains, so
 * precision there is false precision anyway.
 *
 * It must NEVER touch page counts, the corpus review total, «حساب N» or `otherPagesTouched`.
 * Those are EVIDENCE. Humanizing a claim weakens it, and «كتب على ١٤٧ صفحة تانية» is the exact
 * sentence that makes this whole list credible.
 *
 * FLOOR, NEVER ROUND UP. A page that publishes claims about named real businesses must not
 * overstate a figure, so every humanized value is `<=` the true one. Rounding would turn 999,950
 * into «١ مليون», which is a number we did not measure.
 *
 * RETURNS `null` FOR ABSENT DATA, never «٠». The caller renders its own «من غير متابعين» branch,
 * and that branch is the COMMON case on this corpus. Emitting a zero would invent a fact.
 *
 * The unit word stays SINGULAR after a fractional quantity («١٤٫٧ ألف», never «آلاف»): the broken
 * plural is only correct after a bare integer 3-10, and every value in that band here carries a
 * decimal.
 */
export function humanizeCount(value: number | null | undefined): string | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;

  const abs = Math.abs(value);
  if (abs < 1_000) return formatHumanizedNumber(String(value));

  const [unit, word] = abs < 1_000_000 ? [1_000, "ألف"] : [1_000_000, "مليون"];

  // Floor to one decimal. `Math.floor` on the scaled value is what keeps 999,950 at «٩٩٩٫٩ ألف»
  // instead of promoting it to the next unit.
  const scaled = Math.floor((value / unit) * 10) / 10;
  // Strip a trailing «٫٠» — «١٢ ألف», not «١٢٫٠ ألف».
  const text = Number.isInteger(scaled) ? String(scaled) : scaled.toFixed(1);

  return `${formatHumanizedNumber(text)} ${word}`;
}
